import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Edit2, ToggleLeft, ToggleRight, Shield, KeyRound, ShieldOff, SlidersHorizontal } from 'lucide-react'
import { adminUsersApi, rolesApi } from '../../lib/api'
import UserPermissionsModal from './UserPermissionsModal'
import { usePermission } from '../../hooks/usePermission'
import { useAuth } from '../../hooks/useAuth'
import DataTable, { Column } from '../../components/shared/DataTable'
import RoleBadge from '../../components/shared/RoleBadge'
import StatusBadge from '../../components/shared/StatusBadge'
import LoadingState from '../../components/shared/LoadingState'
import ErrorState from '../../components/shared/ErrorState'
import { useToast } from '../../hooks/useToast'
import { formatDatetime } from '../../lib/utils'
import type { AdminUser } from '../../types'
import { tx } from '../../lib/i18n'


const defaultForm = { firstName: '', lastName: '', email: '', password: '', role: 'support' }

const AdminUsersPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const { can } = usePermission()
  const { currentAdmin } = useAuth()
  const [permUser, setPermUser] = useState<AdminUser | null>(null)
  const [createModal, setCreateModal] = useState(false)
  const [editModal, setEditModal] = useState<AdminUser | null>(null)
  const [form, setForm] = useState(defaultForm)
  const [editForm, setEditForm] = useState<{ role: string; isActive: boolean }>({ role: 'support', isActive: true })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminUsersApi.list().then((r) => r.data.data),
  })

  const { data: roles = [] } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => rolesApi.list().then((r) => r.data.data ?? []),
  })
  // only a Super Admin may hand out Super Admin
  const roleOptions = roles
    .filter((r) => r.name !== 'super_admin' || currentAdmin?.role === 'super_admin')
    .sort((a, b) => Number(a.name === 'super_admin') - Number(b.name === 'super_admin')) // Super Admin last
  const roleLabel = (name: string) => roles.find((r) => r.name === name)?.label ?? name

  const admins = data ?? []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiError = (e: any, fallback: string) => e?.response?.data?.error ?? fallback

  const createMutation = useMutation({
    mutationFn: () => adminUsersApi.create(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showToast({ type: 'success', message: tx('Admin user created.') })
      setCreateModal(false)
      setForm(defaultForm)
    },
    onError: (e) => showToast({ type: 'error', message: apiError(e, tx('Failed to create admin.')) }),
  })

  const updateMutation = useMutation({
    mutationFn: () => adminUsersApi.update(editModal!.id, editForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showToast({ type: 'success', message: tx('Admin updated.') })
      setEditModal(null)
    },
    onError: (e) => showToast({ type: 'error', message: apiError(e, tx('Failed to update.')) }),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminUsersApi.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showToast({ type: 'success', message: tx('Status updated.') })
    },
  })


  const reset2faMutation = useMutation({
    mutationFn: (id: string) => adminUsersApi.resetTwoFactor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showToast({ type: 'success', message: 'รีเซ็ต 2FA แล้ว — ผู้ใช้ต้องตั้งค่าใหม่ตอนเข้าสู่ระบบครั้งถัดไป' })
    },
    onError: (e) => showToast({ type: 'error', message: apiError(e, 'Failed to reset 2FA') }),
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => adminUsersApi.resetPassword(id, password),
    onSuccess: () => showToast({ type: 'success', message: 'ตั้งรหัสผ่านใหม่แล้ว' }),
    onError: (e) => showToast({ type: 'error', message: apiError(e, 'Failed to reset password') }),
  })

  const askResetPassword = (u: AdminUser) => {
    const pw = window.prompt(`ตั้งรหัสผ่านใหม่ให้ ${u.email} (อย่างน้อย 8 ตัวอักษร)`)
    if (pw) resetPasswordMutation.mutate({ id: u.id, password: pw })
  }

  const columns: Column<AdminUser>[] = [
    {
      key: 'firstName', header: tx('Name'), sortable: true,
      render: (r) => (
        <div>
          <p className="font-medium text-gray-900">{r.firstName} {r.lastName}</p>
          <p className="text-xs text-gray-400">{r.email}</p>
        </div>
      ),
    },
    {
      key: 'role', header: tx('Role'),
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <RoleBadge role={r.role} label={r.roleLabel ?? roleLabel(r.role)} />
          {r.hasPermissionOverrides && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title={tx('Has personal permission adjustments')}>
              {tx('Adjusted')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'isActive', header: tx('Status'),
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'suspended'} label={r.isActive ? tx('Active') : tx('Inactive')} />,
    },
    {
      key: 'lastLoginAt', header: tx('Last Login'),
      render: (r) => <span className="text-gray-500 text-sm">{formatDatetime(r.lastLoginAt)}</span>,
    },
    {
      key: 'totpEnabled', header: tx('2FA'),
      render: (r) =>
        r.totpEnabled ? (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">เปิด</span>
        ) : (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">ปิด</span>
        ),
    },
    {
      key: 'actions', header: tx('Actions'),
      render: (r) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {can('admin-users.update') && (
            <button
              title={tx('Edit')}
              onClick={() => { setEditModal(r); setEditForm({ role: r.role, isActive: r.isActive }) }}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {can('admin-users.update') && (
            <button
              title={r.isActive ? tx('Deactivate') : tx('Activate')}
              onClick={() => toggleMutation.mutate({ id: r.id, isActive: !r.isActive })}
              className={`p-1.5 rounded-lg transition-colors ${r.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
            >
              {r.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
            </button>
          )}
          {r.role !== 'super_admin' && (
            <button
              title={tx('Personal permissions')}
              onClick={() => setPermUser(r)}
              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              data-testid="user-permissions-open"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
          )}
          {can('admin-users.reset_password') && (
            <button
              title="ตั้งรหัสผ่านใหม่"
              onClick={() => askResetPassword(r)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <KeyRound className="h-4 w-4" />
            </button>
          )}
          {r.totpEnabled && can('admin-users.reset_2fa') && (
            <button
              title="รีเซ็ต 2FA (ผู้ใช้ทำมือถือหาย)"
              onClick={() => {
                if (window.confirm(`รีเซ็ต 2FA ของ ${r.email}?`)) reset2faMutation.mutate(r.id)
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <ShieldOff className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ]

  if (isLoading) return <LoadingState variant="spinner" />
  if (error) return <ErrorState message={tx('Failed to load admin users.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Admin Users')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('Manage internal admin accounts and roles')}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/permissions"
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="open-permissions-page"
          >
            <Shield className="h-4 w-4 text-gray-500" />
            {tx('Roles & Permissions')}
          </Link>
          {can('admin-users.create') && (
            <button
              onClick={() => {
                setForm(defaultForm) // always a blank form — never re-show a typed password
                setCreateModal(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> {tx('Create Admin')}
            </button>
          )}
        </div>
      </div>

      <DataTable columns={columns} data={admins} emptyMessage={tx('No admin users found.')} />

      {permUser && <UserPermissionsModal user={permUser} onClose={() => setPermUser(null)} />}

      {/* Create modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCreateModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Create Admin User')}</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{tx('First Name')}</label>
                  <input type="text" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Last Name')}</label>
                  <input type="text" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Password')}</label>
                <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Role')}</label>
                <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {roleOptions.map((r) => (
                    <option key={r.id} value={r.name}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button onClick={() => createMutation.mutate()} disabled={createMutation.isPending} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {createMutation.isPending ? 'Creating...' : tx('Create Admin')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditModal(null)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Edit')} {editModal.firstName} {editModal.lastName}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Role')}</label>
                <select value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {roleOptions.map((r) => (
                    <option key={r.id} value={r.name}>{r.label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">{tx('Account is Active')}</span>
              </label>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setEditModal(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {updateMutation.isPending ? tx('Saving...') : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsersPage
